import React, { useState } from "react";
import { motion } from "framer-motion";
import placeholder1 from "../../assets/image (2).jpg";
import placeholder2 from "../../assets/smartstart-logo-side-text-3.png";
import { PrimaryButton2 } from "./Buttons";
import { Link } from "react-router-dom";

const HeroSection = () => {
  const [showMore, setShowMore] = useState(false);

  // Animation variants for the rainbow letters
  const letters = ["S", "P", "E", "C", "T", "R", "U", "M"];
  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.06,
      },
    },
  };
  const letter = {
    hidden: { y: 18, opacity: 0, scale: 0.92 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { type: "spring", stiffness: 420, damping: 28 },
    },
    hover: { scale: 1.12, y: -6, transition: { type: "spring", stiffness: 500 } },
  };

  return (
    <section className="px-4 sm:px-6 md:px-8 lg:px-32 py-8 md:py-12">
      <div className="max-w-7xl mx-auto relative">
        {/* floating orbs for playful background */}
        <div className="orb orb--1" aria-hidden="true" />
        <div className="orb orb--2" aria-hidden="true" />
        <div className="orb orb--3" aria-hidden="true" />
        <div className="orb orb--4" aria-hidden="true" />

        {/* TOP ROW: left = Care..., right = logo + SPECTRUM heading */}
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] items-center gap-6 mb-6">
          <div className="text-left flex items-center gap-4 md:col-start-1 md:self-start">
            {/* Logo moved to the left section */}
            <img
              src={placeholder2}
              alt="Spectrum Nurturing Academy logo"
              title="Spectrum Nurturing Academy"
              data-testid="hero-logo"
              className="flex-none block mr-4 h-12 sm:h-14 md:h-16 lg:h-20 w-auto object-contain bg-white p-1 rounded-md shadow-md"
            />
            <h1 className="text-[16px] lg:text-xl font-bold text-gray-800 bg-orange-400 px-3 py-2 rounded inline-block">
              Care – Educate – Empower
            </h1>
          </div>

          <div className="flex flex-col items-end text-right md:pr-12 lg:pr-24 md:col-start-2">
            <div className="pt-2 lg:pt-6">
              <motion.div
                className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl flex flex-row flex-wrap justify-end gap-0 leading-none font-bold"
                variants={container}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.5 }}
                aria-hidden="true"
              >
                {letters.map((l, i) => (
                  <motion.span
                    key={i}
                    className={`inline-block ${
                      i === 0 ? "text-sky-400" : i === 1 ? "text-orange-400" : i === 2 ? "text-red-500" : i === 3 ? "text-purple-500" : i === 4 ? "text-yellow-400" : i === 5 ? "text-violet-500" : i === 6 ? "text-teal-500" : "text-pink-500"
                    }`}
                    variants={letter}
                    whileHover="hover"
                    aria-hidden={false}
                    title={l}
                  >
                    {l}
                  </motion.span>
                ))}
              </motion.div>

              <div className="mt-2">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-500">
                  NURTURING
                </div>
                <div className="text-xl sm:text-2xl text-sky-400">ACADEMY</div>
              </div>
            </div>
          </div>
        </div>

        {/* SECOND ROW: left = paragraphs/buttons, right = main photo */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_520px] items-center gap-8">
          <div className="w-full text-left min-w-0">
            {/* Intro paragraphs (left column) */}
            <p className="text-base sm:text-lg text-gray-700 mb-4 text-justify">
              At Spectrum Nurturing Academy, we are committed to empowering children
              with autism and developmental differences by providing a safe,
              structured, and nurturing environment where they can thrive.
            </p>

            <p className="text-base sm:text-lg text-gray-700 mb-4 text-justify">
              Founded by experienced Special Educators and Autism Behavior Therapists,
              SNA is more than just a learning center—it’s a place where children aged
              3 to 10 are guided with compassion, respect, and proven strategies to
              reach their fullest potential.
            </p>

            <p className="text-base sm:text-lg text-gray-700 mb-6 text-justify">
              We specialize in preparing children for transition into general
              education, equipping them with the academic, social, and emotional
              skills they need to succeed in life—one small step at a time.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <PrimaryButton2
                label={showMore ? "Hide" : "Learn More"}
                onClick={() => setShowMore((s) => !s)}
                aria-expanded={showMore}
                aria-controls="our-approach"
              />

              <Link to="/contact">
                <PrimaryButton2 label="Contact Us" />
              </Link>
            </div>

            {/* Revealable block: paragraph with image on the right (responsive) */}
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showMore ? "max-h-[800px]" : "max-h-0"}`}>
              <div
                id="our-approach"
                aria-hidden={!showMore}
                className={`mt-6 grid gap-4 ${showMore ? "" : "pointer-events-none"} grid-cols-1 md:grid-cols-2 items-center`}
              >
                <div className="text-gray-700 text-justify">
                  <h3 className="text-xl font-semibold mb-2">Our Approach</h3>
                  <p className="mb-2">
                    We use evidence-based teaching strategies tailored to each child's strengths and needs. Our team works closely with families to set realistic goals and celebrate every achievement.
                  </p>
                  <p>
                    Therapy and learning are integrated into fun, meaningful activities so children build skills naturally and with confidence. Regular progress reviews ensure we stay on track.
                  </p>
                </div>

                <div className="flex items-center justify-center md:justify-end">
                  <img
                    src={placeholder1}
                    alt="Children learning at Spectrum Nurturing Academy"
                    className="w-full md:max-w-[320px] object-cover rounded shadow-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-full flex items-center justify-center md:justify-end overflow-hidden">
            <motion.img
              src={placeholder1}
              alt="Children playing"
              className="w-full max-w-[420px] object-cover shadow-[12px_12px_20px_rgba(0,0,0,0.25)] rounded object-center img-tilt"
              whileHover={{ scale: 1.03, rotate: -2 }}
              transition={{ type: "spring", stiffness: 260 }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
